import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@app/models/user';
import { UserService } from '@app/services/api/user.service';
import { FlashService } from '@app/services/flash.service';

import {
  AddOne,
  Create,
  CreateFail,
  CreateSuccess,
  ExportUser,
  FetchUsersByType,
  FetchUsersByTypeFail,
  FetchUsersByTypeSuccess,
  LoadFail,
  LoadSelfFail,
  LoadSelfSuccess,
  LoadSuccess,
  Update,
  UpdateBusiness,
  UpdateBusinessFail,
  UpdateBusinessSuccess,
  UpdateFail,
  UpdateOne,
  UpdateSuccess,
  UserActionTypes
} from '@core/actions/user.actions';

import { SetMe } from '@app/modules/core/actions/me.actions';
import { ApiService } from '@app/services/api/api.service';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class UserEffects {
  @Effect()
  load$: Observable<Action> = this.actions$.pipe(
    ofType(UserActionTypes.Load),
    switchMap(() =>
      this.usersApi.get().pipe(
        map((users: User[]) => new LoadSuccess(users)),
        catchError(error => of(new LoadFail(error)))
      )
    )
  );

  @Effect()
  loadSelf$: Observable<Action> = this.actions$.pipe(
    ofType(UserActionTypes.LoadSelf),
    switchMap(() =>
      this.usersApi.getMe().pipe(
        map((user: User) => new LoadSelfSuccess(user)),
        catchError(error => of(new LoadSelfFail(error)))
      )
    )
  );

  @Effect()
  loadSelfSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSelfSuccess>(UserActionTypes.LoadSelfSuccess),
    map(action => action.user),
    map(user => new SetMe(user))
  );

  @Effect()
  create$: Observable<Action> = this.actions$.pipe(
    ofType<Create>(UserActionTypes.Create),
    map(action => action.user),
    switchMap(user =>
      this.usersApi.create(user).pipe(
        map((savedUser: User) => new CreateSuccess(savedUser)),
        catchError(error => of(new CreateFail(error)))
      )
    )
  );

  @Effect({ dispatch: false })
  createFail$: Observable<Action> = this.actions$.pipe(
    ofType<CreateFail>(UserActionTypes.CreateFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'No se pudo crear el usuario',
        type: 'danger'
      })
    )
  );

  @Effect()
  createSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<CreateSuccess>(UserActionTypes.CreateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Usuario creado' });
      this.router.navigate(['/users']);
      return new AddOne(action.user);
    })
  );

  @Effect()
  update$: Observable<Action> = this.actions$.pipe(
    ofType<Update>(UserActionTypes.Update),
    map(action => [action.id, action.user]),
    switchMap(([id, data]) => {
      return this.usersApi.update(id, data).pipe(
        map((users: User) => new UpdateSuccess(id, users)),
        catchError(error => of(new UpdateFail(error)))
      );
    })
  );

  @Effect()
  updateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateSuccess>(UserActionTypes.UpdateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Usuario actualizado' });
      this.router.navigate(['/users']);
      return new UpdateOne(action.data);
    })
  );

  @Effect()
  updateBusiness$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateBusiness>(UserActionTypes.UpdateBusiness),
    map(action => [action.id, action.data]),
    switchMap(([id, data]) => {
      return this.usersApi.updateBusiness(id, data).pipe(
        map((users: User) => new UpdateBusinessSuccess(id, users)),
        catchError(error => of(new UpdateBusinessFail(error)))
      );
    })
  );

  @Effect()
  updateBusinessSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateBusinessSuccess>(UserActionTypes.UpdateBusinessSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Cambio UDN/País completado' });
      // this.router.navigate(['/follows']);
      window.location.reload();
      return new UpdateOne(action.data);
    })
  );

  @Effect({ dispatch: false })
  exportUser$ = this.actions$.pipe(
    ofType<ExportUser>(UserActionTypes.ExportUser),
    map(action => [action.type]),
    tap(
      ([id, data, type]) => this.api.exportFollowUsers(),
      tap(() => console.log('done'))
    )
  );

	
  @Effect()
  fetchUsersByType$: Observable<Action> = this.actions$.pipe(
    ofType<FetchUsersByType>(UserActionTypes.FetchUsersByType),
    map(action => action.typeUser),
    switchMap(typeUser =>
      this.usersApi.getUsersByType(typeUser).pipe(
        map((savedUser) => new FetchUsersByTypeSuccess(savedUser)),
        catchError(error => of(new FetchUsersByTypeFail(error)))
      )
    )
  );

  constructor(
    private actions$: Actions,
    private usersApi: UserService,
    private fs: FlashService,
    private router: Router,
    private api: ApiService
  ) {}
}
